import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";

// --- Form state ---

interface MenuTypeFormState {
  name: string;
  description: string;
  startTime: string;
  endTime: string;
  active: boolean;
  isNextDay: boolean;
  displayOrder: string;
}

const EMPTY_FORM: MenuTypeFormState = {
  name: "",
  description: "",
  startTime: "00:00",
  endTime: "00:00",
  active: true,
  isNextDay: false,
  displayOrder: "0",
};

/** Returns true when end < start (implying next-day span) */
function shouldShowNextDay(start: string, end: string): boolean {
  if (!start || !end) return false;
  return end < start;
}

// --- Component ---

interface MenuTypeEditorProps {
  menuTypeId?: string;
  onBack: () => void;
}

export default function MenuTypeEditor({ menuTypeId, onBack }: MenuTypeEditorProps) {
  const isEdit = !!menuTypeId;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<MenuTypeFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // --- Fetch existing menu type for edit ---

  const { data: existingMenuType, isLoading: isLoadingMenuType } = useQuery<any>({
    queryKey: ["/v1/pos/menu-types", menuTypeId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/menu-types/${menuTypeId}`);
      if (!res.ok) throw new Error("Failed to fetch menu type");
      return res.json();
    },
    enabled: isEdit,
  });

  // Populate form
  useEffect(() => {
    if (!existingMenuType) return;
    const mt = existingMenuType;
    setForm({
      name: mt.name ?? "",
      description: mt.description ?? "",
      startTime: mt.startTime ?? "00:00",
      endTime: mt.endTime ?? "00:00",
      active: mt.active ?? true,
      isNextDay: !!mt.isNextDay,
      displayOrder: mt.displayOrder != null ? String(mt.displayOrder) : "0",
    });
  }, [existingMenuType]);

  // Auto-clear isNextDay when times change
  useEffect(() => {
    if (!shouldShowNextDay(form.startTime, form.endTime) && form.isNextDay) {
      setForm((prev) => ({ ...prev, isNextDay: false }));
    }
  }, [form.startTime, form.endTime]);

  const showNextDay = shouldShowNextDay(form.startTime, form.endTime) || form.isNextDay;

  // --- Field updater ---

  const set = <K extends keyof MenuTypeFormState>(key: K, value: MenuTypeFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // --- Submit ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast({ title: "Validation Error", description: "Menu type name is required", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const body: Record<string, any> = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        startTime: form.startTime,
        endTime: form.endTime,
        active: form.active,
        isNextDay: form.isNextDay,
        displayOrder: form.displayOrder ? Number(form.displayOrder) : 0,
      };

      const url = isEdit ? `/v1/pos/menu-types/${menuTypeId}` : "/v1/pos/menu-types";
      const method = isEdit ? "PUT" : "POST";

      const res = await posApiFetch(url, {
        method,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} menu type`);
      }

      toast({ title: `Menu type ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/menu-types"] });
      onBack();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || `Failed to ${isEdit ? "update" : "create"} menu type`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Loading ---

  if (isEdit && isLoadingMenuType) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Menu Types
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  // --- Render ---

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Menu Types
        </Button>
        <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
          {isEdit ? "Edit Menu Type" : "Create Menu Type"}
        </h2>
        <div className="w-[140px]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        {/* Basic Details */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Basic Details</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Name *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Lunch, Dinner"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Display Order</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  min="0"
                  step="1"
                  value={form.displayOrder}
                  onChange={(e) => set("displayOrder", e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Time Settings */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Time Settings</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Start Time</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="time"
                  value={form.startTime}
                  onChange={(e) => set("startTime", e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">End Time</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="time"
                  value={form.endTime}
                  onChange={(e) => set("endTime", e.target.value)}
                />
              </div>
            </div>
            {showNextDay && (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={form.isNextDay}
                  onCheckedChange={(checked) => set("isNextDay", !!checked)}
                />
                <Label className="text-[12px]">Ends next day</Label>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Description & Status */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Description & Status</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="space-y-1">
              <Label className="text-[12px]">Description</Label>
              <Textarea
                className="text-[13px] min-h-[80px]"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} />
              <Label className="text-[12px]">Active</Label>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-[36px] text-[13px]"
            onClick={onBack}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]"
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update Menu Type" : "Create Menu Type"}
          </Button>
        </div>
      </form>
    </div>
  );
}
